// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Where the hole view gets its aerial imagery.
 *
 * Esri's World Imagery basemap is the default: it needs no key and covers golf courses
 * at the zoom levels this view uses. Its terms require the attribution rendered on the
 * view, so keep that visible if you swap providers — and set the attribution to match.
 *
 * Both are overridable so a deployment can point at its own tile cache or a licensed
 * provider without a code change. Read `process.env` with a plain member access:
 * babel-preset-expo only inlines that form, and `process.env?.X` silently becomes
 * undefined on native.
 */

const DEFAULT_TILE_URL_TEMPLATE =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const DEFAULT_ATTRIBUTION = 'Imagery © Esri, Maxar, Earthstar Geographics';

export const getSatelliteTileUrlTemplate = (): string => {
  const configured = process.env.EXPO_PUBLIC_SATELLITE_TILE_URL;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_TILE_URL_TEMPLATE;
};

export const getSatelliteAttribution = (): string => {
  const configured = process.env.EXPO_PUBLIC_SATELLITE_ATTRIBUTION;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_ATTRIBUTION;
};

/**
 * Where a locator map gets its tiles — roads, towns and coastline, not imagery.
 *
 * Esri again, for the reason the imagery uses Esri: no key, and terms this repo has already
 * read. World_Street_Map is the road basemap; note the `{z}/{y}/{x}` order, which is ArcGIS
 * REST's and not the `{z}/{x}/{y}` most tile servers use.
 *
 * Deliberately a separate source from the satellite one rather than a mode of it. They answer
 * different questions — "what does this hole look like" against "where in the country is this
 * club" — and at the zoom a locator opens on, imagery is an undifferentiated green blur while
 * a road map is legible.
 */
const DEFAULT_STREET_TILE_URL_TEMPLATE =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';

const DEFAULT_STREET_ATTRIBUTION = 'Map © Esri, HERE, Garmin, © OpenStreetMap contributors';

export const getStreetTileUrlTemplate = (): string => {
  const configured = process.env.EXPO_PUBLIC_STREET_TILE_URL;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_STREET_TILE_URL_TEMPLATE;
};

export const getStreetAttribution = (): string => {
  const configured = process.env.EXPO_PUBLIC_STREET_ATTRIBUTION;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_STREET_ATTRIBUTION;
};

/**
 * Credit for the stored whole-course basemap (see HoleMapCanvas/HoleViewModal's
 * `storedBasemap`/`lantmaterietImage`), never the live tile source above. Lantmateriet's
 * Ortofoto is CC BY 4.0, which is what makes storing and redistributing the picture allowed
 * in the first place (see course-analysis-service/README.md's licensing section) — unlike
 * Esri, whose terms permit tracing but not retaining the imagery. That licence requires
 * this credit wherever the image is shown, so it is not overridable like the tile source is.
 */
export const LANTMATERIET_ATTRIBUTION = 'Imagery © Lantmäteriet';

/** The same, for GeoDanmark's Ortofoto — free public data, on the same terms-require-credit footing. */
export const GEODANMARK_ATTRIBUTION = 'Imagery © GeoDanmark';

/**
 * The same, for Spain's PNOA orthophoto (IGN), CC BY 4.0 — in the form IGN's licence sets out
 * ("<product> CC-BY 4.0 <producers>", the producers being the Sistema Cartográfico Nacional its
 * services name), shown with the picture. Verbatim in every language: it is the licence's
 * wording, not a sentence of ours to translate.
 */
export const IGN_PNOA_ATTRIBUTION = 'PNOA CC-BY 4.0 scne.es';

/**
 * The credit a stored basemap from `provider` must carry, or null for a provider whose imagery
 * is never stored (Esri) and so never shown as a stored picture.
 *
 * A lookup rather than a conditional at each call site: a national provider added without its
 * credit would render someone's licensed imagery uncredited, which is the one failure mode the
 * licence actually forbids.
 */
export const storedBasemapAttribution = (provider: string | null | undefined): string | null => {
  switch (provider) {
    case 'lantmateriet-orto':
      return LANTMATERIET_ATTRIBUTION;
    case 'geodanmark-orto':
      return GEODANMARK_ATTRIBUTION;
    case 'ign-pnoa':
      return IGN_PNOA_ATTRIBUTION;
    default:
      return null;
  }
};

/**
 * Whether `basemap` is a national orthophoto whose pictures are stored per course, as opposed to
 * live tiles (Esri) or no picture at all (drawing).
 */
export const isNationalOrtho = (basemap: string | null | undefined): boolean =>
  storedBasemapAttribution(basemap) !== null;
